import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Grid,
  Box,
  TextField,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Tooltip
} from "@material-ui/core";
import {
  Equalizer as EqualizerIcon,
  Settings as SettingsIcon,
  InfoOutlined as InfoOutlinedIcon
} from "@material-ui/icons";
import { DebounceInput } from "react-debounce-input";
import { withRouter } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import _ from "lodash";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import ActionButtons from "./components/ActionButtons";
import ClassGeneralBoard from "./components/ClassGeneralBoard";
import ClassStudentsListViewGrid from "./components/ClassStudentsListViewGrid";
import ClassStudentsListViewToggle, {
  persistStudentsView,
  readStoredStudentsView,
  STUDENTS_VIEW_GRID
} from "./components/ClassStudentsListViewToggle";
import GridFullscreen from "../../components/GridFullscreen";
import Divider from "../../waybee-ui/Divider";
import Heading from "../../waybee-ui/Heading/Heading";
import Group from "../../waybee-ui/Group";
import BackButton from "../../components/BackButton";
import TableGrid from "../../waybee-ui/TableGrid";
import TeacherService from "../../services/TeacherService";
import GeneralBoardService from "../../services/GeneralBoardService";
import { AbilityContext } from "../../acl/can";
import { createClassAbility } from "../../acl/ability";
import {
  DEFAULT_GENERAL_BOARD_CONFIG,
  getBoardHighlights,
  isBoardColumnVisible
} from "../../utils/generalBoardConfig";
import {
  emptyGeneralBoard,
  sumClassesForVisiblePeriods,
  visiblePeriodBoards
} from "../../utils/generalBoardPeriods";

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

const ActionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BoardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const BoardToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BoardClassesTotal = styled.span`
  color: rgba(0, 0, 0, 0.87);
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
`;

const BoardBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  ${({ $fill }) =>
    $fill &&
    css`
      height: 100%;
    `}
`;

const BoardPeriodBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0 0;
  flex: 0 0 auto;
`;

const BoardFullscreenRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 0 12px;
`;

const BoardPeriodStart = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const PeriodSelect = styled(FormControl)`
  && {
    min-width: 260px;
    max-width: 420px;
    margin-top: 0;
  }
`;

const BoardHint = styled(Typography)`
  && {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`;

const BoardEmptyMessage = styled(Typography)`
  && {
    color: rgba(0, 0, 0, 0.6);
    font-size: 16px;
    padding: 32px 16px;
    text-align: center;
  }
`;

const BoardInfoIcon = styled(InfoOutlinedIcon)`
  && {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const BoardGridWrap = styled.div`
  min-height: 0;
  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      display: flex;
      flex-direction: column;

      & > * {
        flex: 1;
        min-height: 0;
        height: 100%;
      }
    `}
`;

const BoardSettingsWrap = styled.div`
  display: inline-flex;
`;

const StudentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const OverviewIconWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const OverviewIconButton = styled(IconButton)`
  && {
    padding: 6px;
    color: ${({ theme }) => theme.color.primary.dark};
  }
  && svg {
    font-size: 36px;
  }
`;

const badgePulse = keyframes`
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.92);
  }
`;

const OverviewBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.error.main};
  border: 1.5px solid #ffffff;
  pointer-events: none;
  animation: ${badgePulse} 2.4s ease-in-out infinite;
`;

function filterStudents(students, filter) {
  const nameQuery = (filter.name || "").trim().toLowerCase();
  const raQuery = String(filter.ra || "")
    .trim()
    .toLowerCase();
  return _.filter(students, student => {
    const matchesName =
      !nameQuery ||
      _.includes((student.name || "").toLowerCase(), nameQuery);
    const matchesRa =
      !raQuery ||
      _.includes(String(student.ra || "").toLowerCase(), raQuery);
    return matchesName && matchesRa;
  });
}

function periodIdsFromList(periods) {
  return (periods || []).map(period => String(period.id));
}

class ClassDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: {
        name: "",
        ra: ""
      },
      students: [],
      loading: false,
      filteredStudents: [],
      classAbility: null,
      boardConfig: DEFAULT_GENERAL_BOARD_CONFIG,
      studentsView: readStoredStudentsView(),
      boardPeriods: [],
      boardVisiblePeriodIds: [],
      boardsByPeriod: {},
      loadingBoard: false
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  setFilter(field, value) {
    const { filter, students } = this.state;
    const nextFilter = {
      ...filter,
      [field]: value
    };
    this.setState({
      filter: nextFilter,
      filteredStudents: filterStudents(students, nextFilter)
    });
  }

  bootstrap = async () => {
    this.setState({ loading: true });
    const { match, alert, t } = this.props;
    const campusAbility = this.context;
    let classAbility = null;

    try {
      classAbility = await createClassAbility(match.params.id, campusAbility);
    } catch (e) {
      console.error(e);
      classAbility = { can: () => false, cannot: () => true };
    }

    this.setState({ classAbility });

    const canListStudents =
      campusAbility && campusAbility.can("listStudents", "diarySpecificClass");
    const canUseBoard =
      campusAbility && campusAbility.can("use module", "classGeneralBoard");

    try {
      if (canListStudents) {
        const students = await TeacherService.getStudentsForClass(
          match.params.id
        );
        this.setState({ students, filteredStudents: students });
      }
    } catch (e) {
      const status = _.get(e, "response.status");
      if (status !== 403) {
        alert.show(t("classes.details.errorFetchStudents"), {
          title: t("common.actions.error")
        });
      }
      console.error(e);
    }

    if (canUseBoard) {
      try {
        const boardConfig = await GeneralBoardService.getConfig();
        this.setState({ boardConfig });
      } catch (e) {
        console.error(e);
        this.setState({ boardConfig: DEFAULT_GENERAL_BOARD_CONFIG });
      }
      await this.loadBoard();
    }

    this.setState({ loading: false });
  };

  loadBoard = async () => {
    const { match, alert, t } = this.props;
    this.setState({ loadingBoard: true });
    try {
      const [boardConfig, data] = await Promise.all([
        GeneralBoardService.getConfig().catch(() => null),
        GeneralBoardService.getBoard(match.params.id)
      ]);
      const periods = data.periods || [];
      const boardsByPeriod = data.boardsByPeriod || {};
      const selectedIds = periodIdsFromList(
        data.selectedPeriods && data.selectedPeriods.length
          ? data.selectedPeriods
          : periods
      );
      this.setState({
        ...(boardConfig ? { boardConfig } : {}),
        boardPeriods: periods,
        boardVisiblePeriodIds: selectedIds,
        boardsByPeriod
      });
    } catch (e) {
      console.error(e);
      alert.show(t("classes.details.boardLoadError"), {
        title: t("common.actions.error")
      });
      this.setState({
        ...emptyGeneralBoard(),
        boardPeriods: [],
        boardVisiblePeriodIds: [],
        boardsByPeriod: {}
      });
    } finally {
      this.setState({ loadingBoard: false });
    }
  };

  changeBoardPeriods = event => {
    const next = event.target.value;
    this.setState({
      boardVisiblePeriodIds: Array.isArray(next)
        ? next.map(String)
        : [String(next)]
    });
  };

  setStudentsListView = view => {
    persistStudentsView(view);
    this.setState({ studentsView: view });
  };

  redirectTo(route) {
    const { history } = this.props;
    history.push(route);
  }

  render() {
    const {
      filter,
      students,
      filteredStudents,
      loading,
      classAbility,
      boardConfig,
      studentsView,
      boardPeriods,
      boardVisiblePeriodIds,
      boardsByPeriod,
      loadingBoard
    } = this.state;
    const { match, t } = this.props;
    const campusAbility = this.context;
    if (!classAbility || loading) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }
    return (
      <Grid container spacing={2}>
        <BackButton to="/classes" />
        <Grid item xs={12}>
          <AbilityContext.Provider value={classAbility}>
            <Group>
              <ActionsHeader>
                <Heading level={1} icon="play_arrow" gutterBottom>
                  {t("classes.details.actionsTitle")}
                </Heading>
                <OverviewIconWrap>
                  <OverviewIconButton
                    aria-label={t("classes.details.overviewAria")}
                    title={t("classes.details.overviewTooltip")}
                    onClick={() => {
                      this.redirectTo(`/classes/${match.params.id}/overview`);
                    }}
                  >
                    <EqualizerIcon />
                  </OverviewIconButton>
                  <OverviewBadge aria-hidden />
                </OverviewIconWrap>
              </ActionsHeader>
              <Divider />
              <ActionButtons classId="" />
            </Group>
          </AbilityContext.Provider>
        </Grid>

        {campusAbility &&
          campusAbility.can("use module", "classGeneralBoard") && (
            <Grid item xs={12}>
              <Group>
                <BoardHeader>
                  <Heading level={1} icon="view_module">
                    {t("classes.details.boardTitle")}
                  </Heading>
                  {campusAbility.can(
                    "use module",
                    "classGeneralBoardSettings"
                  ) && (
                    <BoardSettingsWrap>
                      <OverviewIconButton
                        aria-label={t("classes.details.boardSettingsAria")}
                        title={t("classes.details.boardSettingsTooltip")}
                        onClick={() => {
                          this.redirectTo("/settings/general-board");
                        }}
                      >
                        <SettingsIcon />
                      </OverviewIconButton>
                    </BoardSettingsWrap>
                  )}
                </BoardHeader>
                <GridFullscreen
                  minHeight={Math.min(
                    550,
                    Math.max(220, 52 + students.length * 50)
                  )}
                  renderToolbar={trigger => {
                    const periodBoards = visiblePeriodBoards(
                      boardPeriods,
                      boardVisiblePeriodIds,
                      boardsByPeriod
                    );
                    const classesTotal = sumClassesForVisiblePeriods(
                      periodBoards
                    );
                    return (
                      <BoardToolbar>
                        <BoardPeriodBar>
                          <BoardPeriodStart>
                            {boardPeriods.length > 0 && (
                              <PeriodSelect>
                                <InputLabel id="general-board-period-label">
                                  {t("common.labels.period")}
                                </InputLabel>
                                <Select
                                  labelId="general-board-period-label"
                                  multiple
                                  value={boardVisiblePeriodIds}
                                  onChange={this.changeBoardPeriods}
                                  disabled={loadingBoard}
                                  renderValue={selected => {
                                    return boardPeriods
                                      .filter(periodItem =>
                                        selected
                                          .map(String)
                                          .includes(String(periodItem.id))
                                      )
                                      .map(periodItem => periodItem.name)
                                      .join(", ");
                                  }}
                                >
                                  {boardPeriods.map(periodItem => (
                                    <MenuItem
                                      key={periodItem.id}
                                      value={String(periodItem.id)}
                                    >
                                      <Checkbox
                                        color="primary"
                                        checked={boardVisiblePeriodIds
                                          .map(String)
                                          .includes(String(periodItem.id))}
                                      />
                                      <ListItemText
                                        primary={periodItem.name}
                                      />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </PeriodSelect>
                            )}
                            <Tooltip
                              title={t("classes.details.boardPeriodTotalsHint")}
                            >
                              <BoardHint component="span">
                                <BoardInfoIcon />
                              </BoardHint>
                            </Tooltip>
                            {loadingBoard && (
                              <CircularProgress size={22} />
                            )}
                          </BoardPeriodStart>
                          {boardVisiblePeriodIds.length > 0 && (
                            <BoardClassesTotal>
                              {t("classes.details.boardClassesTotal", {
                                count: classesTotal
                              })}
                            </BoardClassesTotal>
                          )}
                        </BoardPeriodBar>
                        <BoardFullscreenRow>{trigger}</BoardFullscreenRow>
                      </BoardToolbar>
                    );
                  }}
                >
                  {({ isFullscreen }) => (
                    <BoardBody $fill={isFullscreen}>
                      <BoardGridWrap $fill={isFullscreen}>
                        {boardVisiblePeriodIds.length === 0 ? (
                          <BoardEmptyMessage>
                            {t("classes.details.boardPeriodEmpty")}
                          </BoardEmptyMessage>
                        ) : (
                          <ClassGeneralBoard
                            students={students}
                            showCallNumber={isBoardColumnVisible(
                              boardConfig,
                              "callNumber"
                            )}
                            showRa={isBoardColumnVisible(boardConfig, "ra")}
                            showAbsencesDiscipline={isBoardColumnVisible(
                              boardConfig,
                              "absencesDiscipline"
                            )}
                            showAbsencesPercentDiscipline={isBoardColumnVisible(
                              boardConfig,
                              "absencesPercentDiscipline"
                            )}
                            showAbsencesTotal={isBoardColumnVisible(
                              boardConfig,
                              "absencesTotal"
                            )}
                            showAbsencesPercentTotal={isBoardColumnVisible(
                              boardConfig,
                              "absencesPercentTotal"
                            )}
                            showGrades={isBoardColumnVisible(
                              boardConfig,
                              "grades"
                            )}
                            periods={boardPeriods}
                            visiblePeriodIds={boardVisiblePeriodIds}
                            boardsByPeriod={boardsByPeriod}
                            highlights={getBoardHighlights(boardConfig)}
                            fillHeight={isFullscreen}
                            canOpenStudent={campusAbility.can(
                              "use module",
                              "diarySpecificStudent"
                            )}
                            onStudentClick={student => {
                              this.redirectTo(`/student/${student.ra}`);
                            }}
                          />
                        )}
                      </BoardGridWrap>
                    </BoardBody>
                  )}
                </GridFullscreen>
              </Group>
            </Grid>
          )}

        {campusAbility &&
          campusAbility.can("listStudents", "diarySpecificClass") && (
            <Grid item xs={12}>
              <Group>
                <Box>
                  <StudentsHeader>
                    <Heading level={1} icon="group_work" gutterBottom>
                      {t("classes.details.studentsTitle")}
                    </Heading>
                    <ClassStudentsListViewToggle
                      value={studentsView}
                      onChange={this.setStudentsListView}
                    />
                  </StudentsHeader>
                  <Box pb={4} pt={2}>
                    <form noValidate autoComplete="off">
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <DebounceInput
                            minLength={3}
                            debounceTimeout={300}
                            element={TextField}
                            value={filter.name}
                            onChange={e =>
                              this.setFilter("name", e.target.value)}
                            label={t("classes.list.searchByName")}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DebounceInput
                            minLength={3}
                            debounceTimeout={300}
                            element={TextField}
                            value={filter.ra}
                            onChange={e =>
                              this.setFilter("ra", e.target.value)}
                            label={t("classes.details.searchByRa")}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </Box>
                <Box>
                  {studentsView === STUDENTS_VIEW_GRID ? (
                    <ClassStudentsListViewGrid
                      students={filteredStudents}
                      canOpenStudent={campusAbility.can(
                        "use module",
                        "diarySpecificStudent"
                      )}
                      onStudentClick={student => {
                        this.redirectTo(`/student/${student.ra}`);
                      }}
                    />
                  ) : (
                    <TableGrid>
                      {filteredStudents.map(student => (
                        <TableGrid.Row
                          key={student.ra}
                          id={student.id}
                          photo={student.photo}
                          photoName={student.name}
                          minHeight={110}
                          actionIcon
                          button
                          onClick={() => {
                            if (
                              campusAbility.can(
                                "use module",
                                "diarySpecificStudent"
                              )
                            ) {
                              this.redirectTo(`/student/${student.ra}`);
                            }
                          }}
                        >
                          <TableGrid.Title xs={7}>
                            {student.name}
                          </TableGrid.Title>
                          <TableGrid.Text xs={5}>
                            {t("classes.details.ra", { ra: student.ra })}
                          </TableGrid.Text>
                        </TableGrid.Row>
                      ))}
                    </TableGrid>
                  )}
                </Box>
              </Group>
            </Grid>
          )}
      </Grid>
    );
  }
}

ClassDetails.contextType = AbilityContext;

ClassDetails.propTypes = {
  history: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      hash: PropTypes.string
    }),
    push: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(ClassDetails)));
